import React, { useState, useRef } from 'react';
import {
  View,
  StyleSheet,
  Pressable,
  Animated,
  PanResponder,
  Dimensions,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { Audio } from 'expo-av';
import { Ionicons } from '@expo/vector-icons';
import { Theme } from '../../utils/theme';
import Text from './Text';
import { transcribeAudio } from '../../services/audioTranscriptionService';

interface WhatsAppVoiceInputProps {
  onTranscription: (text: string) => void;
  disabled?: boolean;
}

export default function WhatsAppVoiceInput({ 
  onTranscription, 
  disabled = false 
}: WhatsAppVoiceInputProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [duration, setDuration] = useState(0);
  const [showSlideHint, setShowSlideHint] = useState(false);
  const [debugInfo, setDebugInfo] = useState('');
  
  const recordingRef = useRef<Audio.Recording | null>(null);
  const durationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const slideAnimRef = useRef(new Animated.Value(0)).current;
  const scaleAnimRef = useRef(new Animated.Value(1)).current;
  const pulseAnimRef = useRef(new Animated.Value(1)).current;

  const startRecording = async () => {
    try {
      console.log('🎤 Starting recording...');
      setDebugInfo('Requesting permissions...');
      
      const { status } = await Audio.requestPermissionsAsync();
      console.log('🎤 Permission status:', status);
      setDebugInfo(`Permission: ${status}`);
      
      if (status !== 'granted') {
        Alert.alert('Permission required', 'Please grant microphone permission to record voice messages.');
        return false;
      }

      setDebugInfo('Setting audio mode...');
      await Audio.setAudioModeAsync({
        allowsRecordingIOS: true,
        playsInSilentModeIOS: true,
        staysActiveInBackground: true,
      });

      setDebugInfo('Creating recording...');
      const { recording } = await Audio.Recording.createAsync(
        Audio.RecordingOptionsPresets.HIGH_QUALITY
      );
      
      console.log('🎤 Recording created successfully');
      recordingRef.current = recording;
      setIsRecording(true);
      setDuration(0);
      setShowSlideHint(true);
      setDebugInfo('Recording started!');

      // Start duration counter
      durationIntervalRef.current = setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);

      // Start recording pulse animation
      startPulseAnimation();
      
      return true;
    } catch (error) {
      console.error('❌ Failed to start recording:', error);
      setDebugInfo(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      Alert.alert('Error', 'Failed to start recording. Please try again.');
      return false;
    }
  };

  const stopRecording = async (shouldProcess = true) => {
    try {
      console.log('🎤 Stopping recording...');
      if (!recordingRef.current) {
        console.log('🎤 No recording to stop');
        setIsRecording(false);
        setDuration(0);
        return;
      }

      // Clear interval first
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
        durationIntervalRef.current = null;
      }

      // Stop animations
      stopPulseAnimation();
      
      // Stop recording
      await recordingRef.current.stopAndUnloadAsync();
      const uri = recordingRef.current.getURI();
      recordingRef.current = null;

      // Update state
      setIsRecording(false);
      setShowSlideHint(false);

      console.log('🎤 Recording stopped. URI:', uri, 'Duration:', duration);
      setDebugInfo(`Recorded ${duration}s`);

      if (uri && shouldProcess && duration >= 1) {
        await processAudioFile(uri);
      } else {
        setDebugInfo('Recording too short or cancelled');
        setTimeout(() => setDebugInfo(''), 2000);
      }
      
      setDuration(0);
    } catch (error) {
      console.error('❌ Failed to stop recording:', error);
      setDebugInfo(`Stop error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      
      // Force reset state on error
      setIsRecording(false);
      setDuration(0);
      setShowSlideHint(false);
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
        durationIntervalRef.current = null;
      }
      recordingRef.current = null;
      
      Alert.alert('Error', 'Failed to process recording. Please try again.');
    }
  };

  const processAudioFile = async (uri: string) => {
    setIsProcessing(true);
    setDebugInfo('Processing audio...');
    
    try {
      console.log('🎤 Processing audio file:', uri);
      const transcribedText = await transcribeAudio(uri);
      
      if (transcribedText && transcribedText.trim()) {
        console.log('✅ Transcription successful:', transcribedText);
        setDebugInfo('Transcription complete!');
        onTranscription(transcribedText.trim());
        setTimeout(() => setDebugInfo(''), 2000);
      } else {
        throw new Error('Empty transcription result');
      }
      
    } catch (error) {
      console.error('❌ Transcription failed:', error);
      setDebugInfo(`Transcription error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      Alert.alert('Audio Processing Error', 'Failed to process audio. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const startPulseAnimation = () => {
    Animated.loop(
      Animated.sequence([
        Animated.timing(pulseAnimRef, {
          toValue: 1.2,
          duration: 600,
          useNativeDriver: true,
        }),
        Animated.timing(pulseAnimRef, {
          toValue: 1,
          duration: 600,
          useNativeDriver: true,
        }),
      ])
    ).start();
  };

  const stopPulseAnimation = () => {
    pulseAnimRef.stopAnimation();
    Animated.timing(pulseAnimRef, {
      toValue: 1,
      duration: 200,
      useNativeDriver: true,
    }).start();
  };

  // Simple tap handler for debugging
  const handleSimpleTap = async () => {
    console.log('🎤 Simple tap handler called, isRecording:', isRecording);
    setDebugInfo('Tap detected!');
    
    if (isRecording) {
      console.log('🎤 Stopping recording via tap');
      await stopRecording(true);
    } else {
      console.log('🎤 Starting recording via tap');
      await startRecording();
    }
  };

  const panResponder = PanResponder.create({
    onStartShouldSetPanResponder: () => {
      console.log('🎤 onStartShouldSetPanResponder called');
      return !disabled && !isProcessing;
    },
    onMoveShouldSetPanResponder: () => {
      console.log('🎤 onMoveShouldSetPanResponder called');
      return isRecording;
    },
    
    onPanResponderGrant: async () => {
      console.log('🎤 onPanResponderGrant called');
      if (disabled || isProcessing) return;
      
      Animated.spring(scaleAnimRef, {
        toValue: 1.1,
        useNativeDriver: true,
      }).start();
      
      const started = await startRecording();
      if (!started) {
        Animated.spring(scaleAnimRef, {
          toValue: 1,
          useNativeDriver: true,
        }).start();
      }
    },
    
    onPanResponderMove: (_, gestureState) => {
      if (!isRecording) return;
      
      const slideDistance = Math.abs(gestureState.dx);
      slideAnimRef.setValue(-slideDistance);
      
      if (slideDistance > 50) {
        setShowSlideHint(false);
      }
    },
    
    onPanResponderRelease: (_, gestureState) => {
      console.log('🎤 onPanResponderRelease called');
      if (!isRecording) return;
      
      Animated.spring(scaleAnimRef, {
        toValue: 1,
        useNativeDriver: true,
      }).start();
      
      Animated.spring(slideAnimRef, {
        toValue: 0,
        useNativeDriver: true,
      }).start();
      
      const slideDistance = Math.abs(gestureState.dx);
      const shouldCancel = slideDistance > 100;
      
      stopRecording(!shouldCancel);
    },
  });

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (isProcessing) {
    return (
      <View style={styles.container}>
        <View style={styles.processingContainer}>
          <ActivityIndicator size="small" color={Theme.colors.primary} />
          <Text variant="caption" style={styles.processingText}>
            Processing audio...
          </Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Debug Info */}
      {debugInfo && (
        <Text variant="caption" style={styles.debugText}>
          {debugInfo}
        </Text>
      )}
      
      {/* Recording Duration Display */}
      {isRecording && (
        <View style={styles.durationDisplay}>
          <View style={styles.recordingDot} />
          <Text variant="caption" style={styles.durationText}>
            {formatDuration(duration)}
          </Text>
        </View>
      )}
      
      {/* Microphone Button */}
      <Animated.View
        style={[
          styles.microphoneContainer,
          {
            transform: [
              { scale: scaleAnimRef },
              ...(isRecording ? [{ scale: pulseAnimRef }] : [])
            ]
          }
        ]}
      >
        <Pressable
          style={[
            styles.microphoneButton,
            isRecording && styles.microphoneButtonRecording,
            disabled && styles.microphoneButtonDisabled,
          ]}
          onPress={handleSimpleTap}
          disabled={disabled || isProcessing}
          {...panResponder.panHandlers}
        >
          <Ionicons 
            name={isRecording ? "stop" : "mic"} 
            size={20} 
            color={
              disabled 
                ? Theme.colors.textDisabled 
                : isRecording 
                  ? Theme.colors.textInverse 
                  : Theme.colors.textSecondary
            } 
          />
        </Pressable>
      </Animated.View>
      
      {/* Slide to cancel hint */}
      {isRecording && showSlideHint && (
        <Text variant="caption" style={styles.slideHint}>
          Hold and slide left to cancel
        </Text>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: 'relative',
  },
  debugText: {
    position: 'absolute',
    top: -25,
    left: 0,
    right: 0,
    textAlign: 'center',
    color: Theme.colors.error,
    fontSize: 10,
  },
  durationDisplay: {
    position: 'absolute',
    top: -25,
    right: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: Theme.colors.surface,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: Theme.borderRadius.sm,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  recordingDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: Theme.colors.error,
  },
  durationText: {
    color: Theme.colors.textPrimary,
    fontFamily: Theme.typography.fontFamily.medium,
    fontSize: 12,
  },
  microphoneContainer: {
    // Container for animations
  },
  microphoneButton: {
    width: 40,
    height: 40,
    borderRadius: Theme.borderRadius.sm,
    backgroundColor: Theme.colors.surface,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: Theme.colors.border,
  },
  microphoneButtonRecording: {
    backgroundColor: Theme.colors.error,
    borderColor: Theme.colors.error,
  },
  microphoneButtonDisabled: {
    opacity: 0.6,
  },
  slideHint: {
    position: 'absolute',
    top: 45,
    left: -20,
    right: -20,
    textAlign: 'center',
    color: Theme.colors.textSecondary,
    fontStyle: 'italic',
    fontSize: 10,
  },
  processingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Theme.spacing.sm,
    paddingVertical: Theme.spacing.sm,
  },
  processingText: {
    color: Theme.colors.textSecondary,
  },
}); 